import React, { useContext, useEffect, useState } from 'react'
import { useNavigate }                             from 'react-router-dom'
import {
	Box,
	Collapse,
	IconButton,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import {
	ArrowBack,
	ExpandLess,
	ExpandMore,
} from '@mui/icons-material'
import Grid                from '@mui/material/Unstable_Grid2'
import { LineChart }       from '@mui/x-charts/LineChart'
import { PrepKitContext } from './PrepKit'
import { findFirstQuestionInTopic } from '../../util/mini'
import { splitProductsByType }     from '../../util/product-types'
import { doGet }           from '../../util/do-fetch'
import { logger }          from '../../util/log-utils'
import { PASSING, STAR_AT } from '../../util/readiness'
import { Helmet }          from 'react-helmet-async'

const log = logger('ProgressReport', 1)

const pct = (val) => `${Math.round((val || 0) * 100)}%`

/*
 * Accuracy bands (180): 75%+ green, 60-74% gold, under 60% red, and a star at 90%+.
 *
 * 75 comes from util/readiness's PASSING, so the report grades against the same bar as the topic
 * rows and the readiness meter rather than a number of its own. It was 80 here, which meant a topic
 * at 78% read as passing on the dashboard and as mid-band in the report.
 *
 * THE RED/GOLD BOUNDARY IS 60 HERE AND 65 ON THE DASHBOARD (scoreTier, SCORE_AMBER_BELOW). A topic
 * at 62% therefore reads gold in this report and red on the dashboard. Specified that way; noted
 * because the two are one edit from agreeing if that turns out to be unintended.
 */
const ACC_NEAR_FLOOR = 0.60
const ACC_PASS = PASSING / 100
const ACC_STAR = STAR_AT / 100

// bar, fill and border
const accuracyToken = (val, custom) => {
	if (val >= ACC_PASS) return custom.status.green
	if (val >= ACC_NEAR_FLOOR) return custom.status.gold
	return custom.status.danger
}

/*
 * The same bands as TEXT. Identical to the bar colour in two of three; the mid band is the exception
 * because #FFCC00 measures 1.51:1 on white — a fine bar and an unreadable number. goldText is the
 * lightest gold that clears 4.5:1, so it stays gold rather than becoming the brown this replaced.
 */
const accuracyTextToken = (val, custom) => {
	if (val >= ACC_PASS) return custom.status.green
	if (val >= ACC_NEAR_FLOOR) return custom.status.goldText
	return custom.status.danger
}

// the star rides on the value itself, so it appears wherever an accuracy is printed
const accuracyLabel = (val) => `${pct(val)}${val >= ACC_STAR ? ' ⭐' : ''}`

// dashboard HUD tile: flat surface, 1px border, 3px accent on the left
function StatTile({ label, value, accent, tooltip, subtext, custom }) {
	return (
		<Tooltip title={tooltip || ''} placement="top" arrow disableHoverListener={!tooltip}>
			<Box sx={{
				height: '100%',
				background: custom.bg.hud,
				border: `1px solid ${custom.border.color}`,
				borderLeft: `3px solid ${accent}`,
				borderRadius: `${custom.radius.md}px`,
				p: 1.75,
				cursor: tooltip ? 'help' : 'default',
			}}>
				<Typography sx={{fontSize: custom.fontSize.display, fontWeight: custom.fontWeight.bold, color: custom.text.main, lineHeight: 1.2}}>
					{value}
				</Typography>
				<Typography sx={{fontSize: custom.fontSize.sm, fontWeight: custom.fontWeight.semibold, color: custom.text.main}}>
					{label}
				</Typography>
				{subtext && (
					<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted}}>{subtext}</Typography>
				)}
			</Box>
		</Tooltip>
	)
}

// section label, matching the dashboard's uppercase muted headings
const SectionTitle = ({ children, custom, right }) => (
	<Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5}}>
		<Typography sx={{
			fontSize: custom.fontSize.lg,
			fontWeight: custom.fontWeight.semibold,
			textTransform: 'uppercase',
			color: custom.text.muted,
		}}>
			{children}
		</Typography>
		{right}
	</Box>
)

// what the meter colours mean, so the greens and ambers are not a guessing game
const MetricLegend = ({ custom }) => {
	const item = (color, label) => (
		<Box display="flex" alignItems="center" gap={0.625}>
			<Box sx={{width: 18, height: 5, borderRadius: 3, background: color, flexShrink: 0}}/>
			<Typography sx={{fontSize: custom.fontSize.xxs, color: custom.text.muted, whiteSpace: 'nowrap'}}>
				{label}
			</Typography>
		</Box>
	)
	return (
		<Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
			{item(custom.hud.readiness, '% Complete')}
			{/* the word is dropped: every band below is an accuracy band, and the row is read as one (184) */}
			{item(custom.status.green, '90%+ ⭐')}
			{item(custom.status.green, '75%+')}
			{item(custom.status.gold, '60–74%')}
			{item(custom.status.danger, 'Under 60%')}
		</Box>
	)
}


/* ---------- product / section / topic rollups (from the scored prepkit in context) ---------- */

// scorePrepKit (util/mini.js) sets these counts at every level, including never-answered topics
const stats = (node) => {
	const total    = node.questionCount || 0
	const answered = node.answeredCount || 0
	const correct  = node.correctCount || 0
	return {
		total,
		answered,
		correct,
		accuracy: answered > 0 ? correct / answered : 0,
		coverage: total > 0 ? answered / total : 0,
	}
}

// label + bar + value, coloured by the same thresholds used across the app
// fixed width (not flex) so the product header and the section rows share a metric grid
const METRIC_WIDTH = {xs: 120, sm: 190}

const MetricRow = ({ label, value, valueLabel, accent, labelColor, custom, compact }) => (
	<Box sx={{width: compact ? {xs: 96, sm: 150} : METRIC_WIDTH, flexShrink: 0}}>
		<Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1}>
			<Typography sx={{
				fontSize: custom.fontSize.xxs,
				fontWeight: custom.fontWeight.semibold,
				textTransform: 'uppercase',
				letterSpacing: '0.06em',
				color: custom.text.muted,
			}}>
				{label}
			</Typography>
			<Typography sx={{
				fontSize: compact ? custom.fontSize.xs : custom.fontSize.sm,
				fontWeight: custom.fontWeight.bold,
				// falls back to accent, so every caller whose accent is already legible is unchanged
				color: labelColor || accent,
			}}>
				{valueLabel}
			</Typography>
		</Box>
		<Box sx={{height: compact ? 4 : 6, borderRadius: 3, background: custom.progressEmpty, overflow: 'hidden', mt: 0.4}}>
			<Box sx={{height: '100%', width: `${Math.min(value * 100, 100)}%`, background: accent, borderRadius: 3, transition: 'width 250ms'}}/>
		</Box>
	</Box>
)

const TopicRow = ({ topic, custom, onStudy }) => {
	const s = stats(topic)
	const clickable = s.total > 0
	return (
		<Box
			onClick={() => clickable && onStudy(topic)}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 2,
				flexWrap: 'wrap',
				px: 1.5,
				py: 1,
				borderRadius: `${custom.radius.sm}px`,
				cursor: clickable ? 'pointer' : 'default',
				'&:hover': clickable ? {background: custom.bg.dropdownHover} : undefined,
			}}
		>
			<Typography sx={{flex: 1, minWidth: 160, fontSize: custom.fontSize.sm, color: custom.text.main}}>
				{topic.name}
			</Typography>
			{
				s.answered === 0
					? <Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted}}>Not started</Typography>
					: <>
						{/* abbreviated, because the compact row has room for about four characters (187) */}
						<MetricRow compact custom={custom} label="Score" value={s.accuracy}
						           valueLabel={accuracyLabel(s.accuracy)} accent={accuracyToken(s.accuracy, custom)}
						           labelColor={accuracyTextToken(s.accuracy, custom)}/>
						<MetricRow compact custom={custom} label="% Comp" value={s.coverage}
						           valueLabel={`${s.answered}/${s.total}`} accent={custom.hud.readiness}/>
					</>
			}
		</Box>
	)
}

const SectionRow = ({ section, custom, expanded, onToggle, onStudy }) => {
	const s = stats(section)
	const children = section.topics || []
	const canExpand = children.length > 0

	return (
		<Box sx={{'&:not(:last-of-type)': {borderBottom: `1px solid ${custom.border.color}`}}}>
			<Box
				onClick={() => canExpand && onToggle(section.id)}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 2,
					flexWrap: 'wrap',
					px: 2,
					py: 1.5,
					cursor: canExpand ? 'pointer' : 'default',
					'&:hover': canExpand ? {background: custom.bg.dropdownHover} : undefined,
				}}
			>
				<Typography sx={{
					flex: 1,
					minWidth: 180,
					fontSize: custom.fontSize.sm,
					fontWeight: custom.fontWeight.semibold,
					color: custom.text.main,
				}}>
					{section.name}
				</Typography>

				<MetricRow custom={custom} label="Score" value={s.accuracy}
				           valueLabel={s.answered > 0 ? accuracyLabel(s.accuracy) : '—'}
				           accent={s.answered > 0 ? accuracyToken(s.accuracy, custom) : custom.text.muted}
				           labelColor={s.answered > 0 ? accuracyTextToken(s.accuracy, custom) : custom.text.muted}/>
				<MetricRow custom={custom} label="% Complete" value={s.coverage}
				           valueLabel={`${s.answered}/${s.total}`} accent={custom.hud.readiness}/>

				<Box sx={{width: 24, display: 'flex', justifyContent: 'center', color: custom.text.muted}}>
					{canExpand && (expanded ? <ExpandLess fontSize="small"/> : <ExpandMore fontSize="small"/>)}
				</Box>
			</Box>

			{canExpand && (
				<Collapse in={expanded} timeout="auto" unmountOnExit>
					{/* indented behind a rule so the nesting is obvious at a glance */}
					<Box sx={{
						background: custom.bg.cardInner,
						px: 2,
						py: 1,
						borderTop: `1px solid ${custom.border.color}`,
					}}>
						<Box sx={{borderLeft: `2px solid ${custom.border.strong}`, pl: 1.5}}>
							{children.map(t => (
								<TopicRow key={t.id} topic={t} custom={custom} onStudy={onStudy}/>
							))}
						</Box>
					</Box>
				</Collapse>
			)}
		</Box>
	)
}

const ProductCard = ({ product, custom, expandedSections, onToggle, onStudy }) => {
	const s = stats(product)
	return (
		<Box sx={{
			background: custom.bg.surface,
			border: `1px solid ${custom.border.color}`,
			borderRadius: `${custom.radius.lg}px`,
			boxShadow: custom.shadow.card,
			overflow: 'hidden',
			mb: 2,
		}}>
			{/* product header — the rolled-up view */}
			<Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', p: 2}}>
				<Box sx={{flex: 1, minWidth: 180}}>
					<Typography sx={{fontSize: custom.fontSize.lg, fontWeight: custom.fontWeight.bold, color: custom.text.main}}>
						{product.name}
					</Typography>
					<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted}}>
						{s.answered} of {s.total} questions answered
					</Typography>
				</Box>
				<MetricRow custom={custom} label="Score" value={s.accuracy}
				           valueLabel={s.answered > 0 ? accuracyLabel(s.accuracy) : '—'}
				           accent={s.answered > 0 ? accuracyToken(s.accuracy, custom) : custom.text.muted}
				           labelColor={s.answered > 0 ? accuracyTextToken(s.accuracy, custom) : custom.text.muted}/>
				<MetricRow custom={custom} label="% Complete" value={s.coverage}
				           valueLabel={pct(s.coverage)} accent={custom.hud.readiness}/>
				{/* matches the chevron column on the section rows below, so the meters line up */}
				<Box sx={{width: 24, flexShrink: 0}}/>
			</Box>

		{/*
			  * Divider band between the product rollup and its sections — without it the product
			  * row and the section rows read as one flat list of equals.
			  */}
			<Box sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				px: 2,
				py: 0.75,
				background: custom.bg.cardInner,
				borderTop: `1px solid ${custom.border.color}`,
				borderBottom: `1px solid ${custom.border.color}`,
			}}>
				<Typography sx={{
					fontSize: custom.fontSize.xxs,
					fontWeight: custom.fontWeight.semibold,
					textTransform: 'uppercase',
					letterSpacing: '0.08em',
					color: custom.text.muted,
				}}>
					Sections
				</Typography>
				<Typography sx={{fontSize: custom.fontSize.xxs, color: custom.text.muted}}>
					({(product.topics || []).length})
				</Typography>
			</Box>

			{(product.topics || []).map(section => (
				<SectionRow
					key={section.id}
					section={section}
					custom={custom}
					expanded={expandedSections.has(section.id)}
					onToggle={onToggle}
					onStudy={onStudy}
				/>
			))}
		</Box>
	)
}

/*
 * Supplemental + media products (068). These are excluded from the score metrics above, so
 * they deliberately do NOT get the Accuracy/Coverage meter grid that primary ProductCards
 * carry -- showing the same meters would imply they count toward progress. Flat container,
 * no card shadow, smaller heading. Still expandable to the same SectionRows.
 */
const SupplementalSection = ({ products, custom, expandedSections, onToggle, onStudy }) => {
	if (!products || products.length === 0) return null

	return (
		<Box sx={{mt: 3}}>
			<Typography sx={{
				fontSize: custom.fontSize.sm,
				fontWeight: custom.fontWeight.semibold,
				textTransform: 'uppercase',
				letterSpacing: '0.08em',
				color: custom.text.muted,
			}}>
				Supplemental Study Materials
			</Typography>
			<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted, mb: 1}}>
				Not counted toward the progress metrics above.
			</Typography>
			<Box sx={{
				border: `1px solid ${custom.border.color}`,
				borderRadius: `${custom.radius.md}px`,
				overflow: 'hidden',
			}}>
				{products.map((product, i) => (
					<SupplementalProductRow
						key={product.id}
						product={product}
						custom={custom}
						divider={i > 0}
						expandedSections={expandedSections}
						onToggle={onToggle}
						onStudy={onStudy}
					/>
				))}
			</Box>
		</Box>
	)
}

const SupplementalProductRow = ({ product, custom, divider, expandedSections, onToggle, onStudy }) => {
	const [open, setOpen] = useState(false)
	const s = stats(product)
	const sections = product.topics || []

	return (
		<Box sx={divider ? {borderTop: `1px solid ${custom.border.color}`} : undefined}>
			<Box
				onClick={() => sections.length > 0 && setOpen(o => !o)}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1.5,
					px: 2,
					py: 1.5,
					cursor: sections.length > 0 ? 'pointer' : 'default',
					'&:hover': sections.length > 0 ? {background: custom.bg.dropdownHover} : undefined,
				}}
			>
				<Box sx={{flex: 1, minWidth: 180}}>
					<Typography sx={{fontSize: custom.fontSize.base, fontWeight: custom.fontWeight.semibold, color: custom.text.main}}>
						{product.name}
					</Typography>
					<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted}}>
						{s.answered} of {s.total} questions answered
					</Typography>
				</Box>
				<Box sx={{width: 24, flexShrink: 0, color: custom.text.muted, display: 'flex', alignItems: 'center'}}>
					{sections.length > 0 && (open ? <ExpandLess fontSize="small"/> : <ExpandMore fontSize="small"/>)}
				</Box>
			</Box>

			{open && sections.map(section => (
				<SectionRow
					key={section.id}
					section={section}
					custom={custom}
					expanded={expandedSections.has(section.id)}
					onToggle={onToggle}
					onStudy={onStudy}
				/>
			))}
		</Box>
	)
}

function ProgressReport() {
	const prepKit  = useContext(PrepKitContext)
	const navigate = useNavigate()
	const theme    = useTheme()
	const custom   = theme.custom
	const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('md'))

	const [report, setReport]                     = useState(null)
	const [loading, setLoading]                   = useState(true)
	const [expandedSections, setExpandedSections] = useState(new Set())

	// primary products keep the full metric grid; supplemental + media are grouped below (068)
	const {primary: primaryProducts, supplemental: supplementalProducts} =
		splitProductsByType(prepKit?.products)

	useEffect(() => {
		if (!prepKit) return
		const fetch = async () => {
			try {
				const response = await doGet(`/prepkit/${prepKit.id}/report`)
				const data     = await response.json()
				setReport(data)
			} catch (err) {
				log.error(err)
			} finally {
				setLoading(false)
			}
		}
		fetch()
	}, [prepKit])

	const toggleSection = (sectionId) => {
		setExpandedSections(prev => {
			const next = new Set(prev)
			next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId)
			return next
		})
	}

	// clicking a topic picks up where they left off in it, like the dashboard pills
	const handleStudyTopic = (topic) => {
		const q = findFirstQuestionInTopic(topic, x => !x.userAnswer) || findFirstQuestionInTopic(topic)
		if (q) navigate(`/prepkit/${prepKit.id}/t/${topic.id}/q/${q.code}`)
	}

	const noRounds = report && report.summary.totalRounds === 0

	const coverage = report && report.summary.totalQuestionsAvailable > 0
		? report.summary.uniqueQuestionsAttempted / report.summary.totalQuestionsAvailable
		: 0

	const chartDates    = report ? report.accuracyOverTime.map(d => d.date)    : []
	const chartAccuracy = report ? report.accuracyOverTime.map(d => Math.round((d.accuracy || 0) * 100)) : []
	const showChart     = !noRounds && chartDates.length > 1
	const chartYMin     = chartAccuracy.length > 0 ? Math.min(50, Math.floor(Math.min(...chartAccuracy) / 10) * 10) : 0

	return (
		/* same width and padding as the dashboard so the two screens feel like one product */
		<Box sx={{minHeight: '100%', width: '100%', alignSelf: 'stretch', minWidth: 0}}>
			<Helmet>
				<title>{prepKit ? `Study Insights - ${prepKit.name} - Birdsy` : 'Study Insights'}</title>
			</Helmet>
			<Box sx={{
				width: {xs: '100%', md: 900, lg: 1040},
				maxWidth: '100%',
				mx: 'auto',
				px: {xs: 2, sm: 3},
				py: isSmallScreen ? 2 : 4,
			}}>

			{/* Header */}
			<Box display="flex" alignItems="center" gap={1} mb={3}>
				<IconButton onClick={() => navigate(-1)} sx={{color: custom.text.main}}>
					<ArrowBack />
				</IconButton>
				<Box>
					<Typography sx={{fontSize: custom.fontSize.display, fontWeight: custom.fontWeight.bold, color: custom.text.main}}>
						🎯 Study Insights
					</Typography>
					{prepKit && (
						<Typography sx={{fontSize: custom.fontSize.sm, color: custom.text.muted}}>{prepKit.name}</Typography>
					)}
				</Box>
			</Box>

			{loading && (
				<Typography sx={{fontSize: custom.fontSize.sm, color: custom.text.muted}}>Loading…</Typography>
			)}

			{!loading && noRounds && (
				<Box sx={{
					background: custom.bg.hud,
					border: `1px solid ${custom.border.color}`,
					borderLeft: `3px solid ${custom.hud.readiness}`,
					borderRadius: `${custom.radius.md}px`,
					p: 2,
					fontSize: custom.fontSize.sm,
					color: custom.text.main,
				}}>
					Answer your first question to see your insights here.
				</Box>
			)}

			{!loading && report && !noRounds && (
				<>
					{/* Summary tiles */}
					<Grid container spacing={1.5} mb={4}>
						<Grid xs={12} sm={4}>
							<StatTile
								custom={custom}
								label="Score"
								value={accuracyLabel(report.summary.overallAccuracy)}
								accent={accuracyToken(report.summary.overallAccuracy, custom)}
								tooltip="Your percentage of correct answers across all attempts"
								subtext={`${report.summary.totalAttempts} attempts · ${report.summary.uniqueQuestionsAttempted} questions`}
							/>
						</Grid>
						<Grid xs={12} sm={4}>
							<StatTile
								custom={custom}
								label="Progress"
								value={pct(coverage)}
								accent={accuracyToken(coverage, custom)}
								tooltip="How much of the available study material you've attempted at least once"
								subtext={`${report.summary.uniqueQuestionsAttempted} of ${report.summary.totalQuestionsAvailable} questions`}
							/>
						</Grid>
						<Grid xs={12} sm={4}>
							<StatTile
								custom={custom}
								label="Study Days"
								value={report.summary.totalRounds}
								accent={custom.hud.streak}
								tooltip="Days you have worked through material in this prepkit"
							/>
						</Grid>
					</Grid>

					{/* Score Over Time chart */}
					{showChart && (
						<Box mb={4}>
							<SectionTitle custom={custom}>Score Over Time</SectionTitle>
							<Box sx={{
								background: custom.bg.surface,
								border: `1px solid ${custom.border.color}`,
								borderRadius: `${custom.radius.md}px`,
								px: 1,
								pt: 1,
							}}>
								<LineChart
									height={260}
									xAxis={[{ scaleType: 'point', data: chartDates }]}
									yAxis={[{ min: chartYMin, max: 100, label: 'Score %' }]}
									series={[{
										data: chartAccuracy,
										label: 'Score %',
										color: custom.hud.readiness,
										showMark: true,
									}]}
								/>
							</Box>
						</Box>
					)}

					{/* score and completion by product → section → topic */}
					<SectionTitle custom={custom} right={<MetricLegend custom={custom}/>}>
						Your Performance
					</SectionTitle>
					{primaryProducts.map(product => (
						<ProductCard
							key={product.id}
							product={product}
							custom={custom}
							expandedSections={expandedSections}
							onToggle={toggleSection}
							onStudy={handleStudyTopic}
						/>
					))}

					<SupplementalSection
						products={supplementalProducts}
						custom={custom}
						expandedSections={expandedSections}
						onToggle={toggleSection}
						onStudy={handleStudyTopic}
					/>
				</>
			)}
			</Box>
		</Box>
	)
}

export default ProgressReport
